import React, { useState, useContext, useEffect } from 'react';
import { Alert, Box, Button, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { MDAWorksheetSelectorFormState } from './MDAWorksheetSelector';
import TPPolarisTable from 'src/components/shared/TPPolarisTable';
import { GlobalAppContext } from 'src/components/App';
import { WizardStepComponent } from './MDAWorksheetSteps';
import {
    COLUMN_DEFINITIONS,
    DataSourceRow,
    dataSourceRowTransformer,
    FILTERING_PROPERTIES,
    TABLE_COLUMNS,
    getColumnDefinitions,
    getFilteringProperties,
} from './model/DataSourceSelection';
import 'src/assets/styles/react-table.scss';

// Step 2: MDA Data Source Selection Table
export const DataSourceSelectionTable: WizardStepComponent<MDAWorksheetSelectorFormState> = (props) => {
    const { services } = useContext(GlobalAppContext);
    const [selectedDataSources, setSelectedDataSources] = useState<DataSourceRow[]>([]);
    const [transformedDataSources, setTransformedDataSources] = useState<DataSourceRow[]>([]);

    // Get worksheetId from the previous step
    const worksheetId = props.initialData?.worksheetId;
    const worksheetVersion = props.initialData?.worksheetVersion;

    const [dataSourceResponse, , dataSourceLoading, dataSourceError] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheetDataSources(
            worksheetId,
            worksheetVersion,
            'fetch'
        );

    // Transform data when response changes
    useEffect(() => {
        if (dataSourceResponse && dataSourceResponse.length > 0) {
            const transformedData = dataSourceRowTransformer(dataSourceResponse);
            setTransformedDataSources(transformedData);
        } else {
            setTransformedDataSources([]);
        }
    }, [dataSourceResponse]);

    // Restore previously selected data source from initialData
    useEffect(() => {
        if (props.initialData?.worksheetDatasourceId && transformedDataSources.length > 0) {
            const previouslySelectedDataSource = transformedDataSources.find(
                (dataSource) => dataSource.datasourceId === props.initialData?.worksheetDatasourceId
            );

            if (previouslySelectedDataSource) {
                setSelectedDataSources([previouslySelectedDataSource]);
            } else {
                // Throw error if initialData has worksheetDatasourceId but data source is not found
                throw new Error(
                    `Previously selected data source with ID "${props.initialData.worksheetDatasourceId}" was not found in the current data source list. The data source may have been deleted or is no longer accessible.`
                );
            }
        }
    }, [props.initialData?.worksheetDatasourceId, transformedDataSources]);

    const handleNext = () => {
        if (selectedDataSources.length > 0) {
            const stepData = {
                worksheetDatasourceId: selectedDataSources[0].datasourceId,
                worksheetDatasourceS3PresignUrl: selectedDataSources[0].s3PresignUrl,
                worksheetExecutionId: selectedDataSources[0].executionId,
                worksheetDatasourceDescription: selectedDataSources[0].description,
            };
            props.onSave(stepData);
        }
    };

    const hasSelectedDataSource = selectedDataSources.length > 0;

    // Define visible content options for preferences
    const visibleContentOptions = Object.values(TABLE_COLUMNS).map((column) => ({
        id: column.id,
        label: column.label,
        editable: false,
    }));

    const defaultPreferences = {
        pageSize: 10,
        visibleColumns: [
            'glAccount',
            'costCenter',
            'companyCode',
            'location',
            'project',
            'channel',
            'productLine',
            'description',
        ],
        wrapLines: false,
        stripedRows: false,
        stickyColumns: { first: 0, last: 0 },
    };

    // Empty state when no data sources or errors
    const tableEmptyState = dataSourceError ? (
        <Box textAlign="center">
            <Alert type="error" header="Unable to load data sources">
                {dataSourceError}
            </Alert>
        </Box>
    ) : !worksheetId ? (
        <Box textAlign="center">
            <Alert type="warning" header="No worksheet selected">
                Please select a worksheet first to view its data sources.
            </Alert>
        </Box>
    ) : (
        <Box textAlign="center">
            <SpaceBetween direction="vertical" size="s">
                <b>No data sources found</b>
                <span>No data sources are available for the selected worksheet.</span>
            </SpaceBetween>
        </Box>
    );

    const footer = (
        <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={props.onCancel}>
                Cancel
            </Button>

            <Button variant="link" onClick={props.onBack}>
                Back
            </Button>

            <Button onClick={handleNext} disabled={!hasSelectedDataSource}>
                Next
            </Button>
        </SpaceBetween>
    );

    return (
        <Box padding={{top: 'l', bottom: 'l', left: 'xxl', right: 'xxl'}}> 
        <TPPolarisTable
            header={<Header variant='h3'> Select a source line from worksheet </Header>}
            variant='borderless'
            contentDensity='compact'
            columnDefinitions={getColumnDefinitions(transformedDataSources)}
            tableItems={transformedDataSources}
            isLoading={dataSourceLoading}
            loadingText="Loading data sources..."
            tableEmptyState={tableEmptyState}
            resizableColumns={true}
            sortingColumn="description"
            filteringProperties={getFilteringProperties(transformedDataSources)}
            filteringEmptyText="No data sources found"
            filteringLoadingText="Loading data sources..."
            preferencesStorageKey="mda-datasource-selection-table"
            selectionType="single"
            selectedItems={selectedDataSources}
            setSelectedItems={({ detail }) => {
                setSelectedDataSources(detail.selectedItems);
            }}
            visibleContentOptions={visibleContentOptions}
            defaultPreferences={defaultPreferences}
            pageSizeOptions={[
                { value: 10, label: '10 data sources' },
                { value: 25, label: '25 data sources' },
                { value: 50, label: '50 data sources' },
            ]}
            footer={footer}
        />
        </Box>
    );
};
